import { ga_start_vec, ga_step_micro_vec, ga_result_vec, ga_cancel_vec } from './pkg/svg_fit.js';
import { gaSliders, statusBar } from './dom.js';

/** Active auto-GA session ID (null when idle). */
export let autoGASession: number | null = null;
/** Whether auto-GA is currently running. */
export let autoGARunning: boolean = false;
/** Active manual GA session ID (null when idle). */
export let gaSessionId: number | null = null;
/** Whether the manual GA (Evolve & Fit button) is currently running. */
export let gaRunning: boolean = false;

/** Max children to evaluate per ga_step_micro_vec call (~5-10ms each). */
const MICRO_BUDGET: number = 20;

/** Cancel the auto-GA session if running. */
export function cancelAutoGA(): void {
  if (!autoGARunning) return;
  autoGARunning = false;
  if (autoGASession !== null) {
    try { ga_cancel_vec(autoGASession); } catch (e) { /* ignore */ }
    autoGASession = null;
  }
}

/** Cancel the manual GA session and reset state. */
export function cancelManualGA(): void {
  if (gaSessionId !== null) {
    try { ga_cancel_vec(gaSessionId); } catch (e) { /* ignore */ }
    gaSessionId = null;
  }
  gaRunning = false;
}

/**
 * Run a GA micro-step loop. Each tick evaluates at most MICRO_BUDGET children,
 * then yields to the browser. This keeps each setTimeout handler under ~10ms.
 */
function gaLoop(
  sessionId: () => number | null,
  cancelSession: (id: number) => void,
  isRunning: () => boolean,
  onStatus: (msg: string) => void,
  onDone: (svgPath: string) => void,
  onError: (msg: string) => void,
  maxGen: number,
  popSize: number,
  label: string,
): void {
  const t0: number = performance.now();
  /** Update status every N micro-steps (roughly every generation). */
  const stepsPerGen: number = Math.ceil(popSize / MICRO_BUDGET);
  const uiInterval: number = Math.max(1, stepsPerGen);
  let stepCount: number = 0;

  const tick = (): void => {
    const sid = sessionId();
    if (sid === null || !isRunning()) return;

    try {
      const r: Float32Array = ga_step_micro_vec(sid, MICRO_BUDGET);
      stepCount++;

      const curGen: number = r[0];
      const fitness: number = r[1];
      const done: boolean = r[2] > 0.5;
      const contourIdx: number = r[3];
      const totalContours: number = r[4];
      const nodes: number = r[5];

      if (stepCount % uiInterval === 0 || done) {
        const dt: string = ((performance.now() - t0) / 1000).toFixed(1);
        onStatus(`${label} contour ${contourIdx}/${totalContours} gen ${curGen}/${maxGen} (${dt}s) nodes ${nodes} fitness ${fitness.toFixed(0)}`);
      }

      if (done) {
        const svgPath: string = ga_result_vec(sid);
        cancelSession(sid);
        onDone(svgPath);
        const zCount: number = svgPath.match(/Z/g)?.length || 0;
        const dt: string = ((performance.now() - t0) / 1000).toFixed(1);
        onStatus(`${label} done in ${dt}s (${zCount} contour${zCount !== 1 ? 's' : ''})`);
        return;
      }
      setTimeout(tick, 0);
    } catch (e: unknown) {
      const msg: string = e instanceof Error ? e.message : String(e);
      onError(msg);
      console.error(e);
    }
  };
  tick();
}

/** Run automatic GA fitting (triggered by Auto GA checkbox). Calls onDone with SVG path on completion. */
export async function runAutoGA(
  rgba: Uint8ClampedArray, imgW: number, imgH: number,
  seedX: number, seedY: number,
  tolerance: number, margin: number, noHoles: boolean,
  wasmReady: Promise<void>,
  onDone: (svgPath: string) => void,
  onError: (msg: string) => void,
): Promise<void> {
  if (!rgba || seedX === null) return;
  await wasmReady;

  statusBar.textContent = 'Auto GA...';

  try {
    const id: number = ga_start_vec(
      rgba, imgW, imgH,
      seedX, seedY,
      tolerance, margin, noHoles,
      parseFloat(gaSliders.penalty.el.value),
      parseInt(gaSliders.overshoot.el.value) / 10,
      parseInt(gaSliders.pop.el.value),
      parseInt(gaSliders.gen.el.value),
    );
    if (id === 0) { statusBar.textContent = 'No contours found'; return; }

    autoGASession = id;
    autoGARunning = true;
    const maxGen: number = parseInt(gaSliders.gen.el.value);
    const popSize: number = parseInt(gaSliders.pop.el.value);

    gaLoop(
      () => autoGASession,
      (sid) => { autoGASession = null; autoGARunning = false; try { ga_cancel_vec(sid); } catch (_) { /* */ } },
      () => autoGARunning,
      (msg) => { statusBar.textContent = msg; },
      onDone,
      onError,
      maxGen,
      popSize,
      'Auto GA',
    );
  } catch (e: unknown) {
    autoGARunning = false;
    const msg: string = e instanceof Error ? e.message : String(e);
    onError(msg);
    console.error(e);
  }
}

/** Run manual GA fitting (Evolve & Fit button). Click again to cancel. */
export async function runManualGA(
  rgba: Uint8ClampedArray, imgW: number, imgH: number,
  seedX: number, seedY: number,
  tolerance: number, margin: number, noHoles: boolean,
  wasmReady: Promise<void>,
  onProgress: (msg: string) => void,
  onDone: (svgPath: string) => void,
  onError: (msg: string) => void,
  gaFitBtn: HTMLButtonElement,
): Promise<void> {
  if (seedX === null || !rgba) return;
  await wasmReady;

  if (gaRunning) {
    cancelManualGA();
    gaFitBtn.textContent = 'Evolve & Fit';
    statusBar.textContent = 'Cancelled';
    return;
  }

  try {
    const id: number = ga_start_vec(
      rgba, imgW, imgH,
      seedX, seedY,
      tolerance, margin, noHoles,
      parseFloat(gaSliders.penalty.el.value),
      parseInt(gaSliders.overshoot.el.value) / 10,
      parseInt(gaSliders.pop.el.value),
      parseInt(gaSliders.gen.el.value),
    );
    if (id === 0) { statusBar.textContent = 'No contours found'; return; }

    gaSessionId = id;
    gaRunning = true;
    gaFitBtn.textContent = 'Cancel';
    const maxGen: number = parseInt(gaSliders.gen.el.value);
    const popSize: number = parseInt(gaSliders.pop.el.value);

    gaLoop(
      () => gaSessionId,
      (sid) => { gaSessionId = null; gaRunning = false; try { ga_cancel_vec(sid); } catch (_) { /* */ } gaFitBtn.textContent = 'Evolve & Fit'; },
      () => gaRunning,
      (msg) => { statusBar.textContent = msg; },
      onDone,
      (msg) => { gaRunning = false; gaFitBtn.textContent = 'Evolve & Fit'; onError(msg); },
      maxGen,
      popSize,
      'GA',
    );
  } catch (e: unknown) {
    gaRunning = false;
    gaFitBtn.textContent = 'Evolve & Fit';
    const msg: string = e instanceof Error ? e.message : String(e);
    onError(msg);
    console.error(e);
  }
}
